<script setup>
import _ from 'lodash';
import { watch, ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import { useTransformsStore } from '../../stores/transforms.store';
import AppLoader from '../../components/AppLoader.vue';
import DynamicField from '../../components/DynamicField.vue';
import AppHeader from '../../components/AppHeader.vue';
import AppFooter from '../../components/AppFooter.vue';
import ErrorMessage from '../../components/ErrorMessage.vue';
import { ChevronRightIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import RowActionMenu from '@/components/RowActionMenu.vue';
import { adjustControlConditionalValues, adjustControlConditionalVisibility } from '@/utils/utils';
import { getTransformIcon } from '@/utils/constants';

const props = defineProps({
    transformType: {
        type: String,
        default: null
    },
    cloneConfig: {
        type: Object,
        default: null
    }
});

const transformsStore = useTransformsStore();
const transformCreatePayload = ref({});
let formError = ref('');
let loading = ref(false);
let loadingList = ref(false);
let advancedMode = ref(false);
const hidePromptTemporary = ref(false);
const router = useRouter();

const hideLateralDescriptionValue = ref(true);

onBeforeRouteLeave((to, from, next) => {
    if (loadingList.value) {
        return next();
    }

    if (hidePromptTemporary.value) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You may have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

const transformTypeTitle = computed(() => {
    if (transformsStore.configs) {
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === props.transformType).length
        );

        return found?.[0]?.display_name;
    }
});

const transformTypeControls = computed(() => {
    if (transformsStore.configs) {
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === props.transformType).length
        );

        return found?.[0]?.config;
    }
});

const handleResize = () => {
    hideLateralDescriptionValue.value = window.innerWidth <= 1024;
};

onMounted(() => {
    handleResize();
});

window.addEventListener('resize', handleResize);

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

onMounted(async () => {
    await getTransformConfigs();

    watch(props, async ({ transformType }) => {
        if (transformType && !transformTypeTitle.value) await getTransformConfigs();
    });
});

const onSaveTransform = () => {
    loading.value = true;

    const config = Object.values(transformCreatePayload.value).reduce((payload, { name, value }) => {
        return {
            ...payload,
            [name]: value
        };
    }, {});

    transformsStore
        .create({
            transform: props.transformType,
            config
        })
        .then(async () => {
            hidePromptTemporary.value = true;
            router.push({ name: 'transforms' });
        })
        .catch((err) => {
            formError.value = err;
        })
        .finally(() => {
            loading.value = false;
            hidePromptTemporary.value = false;
        });
};

const onFieldChanged = ({ value, valid, name }) => {
    const control = transformTypeControls.value.find((x) => x.name == name);
    const index = transformTypeControls.value.findIndex((x) => x.name == name);

    if (control) {
        control.value.current_value = value;
        control.value.valid = valid;

        transformTypeControls.value[index] = control;
    }

    transformCreatePayload.value[name] = {
        value,
        valid,
        name
    };
};

const transformTypeConfig = computed(() => {
    const configurations = transformTypeControls.value;

    if (!configurations) return null;

    let transformConfig = _.cloneDeep(configurations);

    if (props.cloneConfig && transformConfig) {
        const cloneConfig = JSON.parse(props.cloneConfig);

        transformConfig = _.map(transformConfig, (configItem) => {
            if (configItem.encrypt) return configItem;

            const clonedConfigValue = cloneConfig[configItem.name];
            if (_.isNil(clonedConfigValue)) return configItem;

            return {
                ...configItem,
                value: {
                    ...configItem.value,
                    default: clonedConfigValue
                }
            };
        });
    }

    return transformConfig;
});

const visibleBasicTransformConfigControls = computed(() => {
    return _.chain(adjustControlConditionalValues(transformTypeConfig.value))
        .filter((x) => !x.display_advanced)
        .orderBy('order_of_display', 'asc')
        .map((control) => adjustControlConditionalVisibility(control, transformTypeConfig.value)?.control || null)
        .compact()
        .value();
});

const visibleAdvancedTransformConfigControls = computed(() => {
    return _.chain(adjustControlConditionalValues(transformTypeConfig.value))
        .filter((x) => x.display_advanced)
        .orderBy('order_of_display', 'asc')
        .map((control) => adjustControlConditionalVisibility(control, transformTypeConfig.value)?.control || null)
        .compact()
        .value();
});

const enableSave = computed(() => {
    if (props.transformType && transformTypeConfig) {
        return transformTypeConfig.value.reduce((valid, { name, display_in_ui }) => {
            return display_in_ui ? valid && transformCreatePayload.value[name] && transformCreatePayload.value[name].valid : true;
        }, true);
    } else {
        return false;
    }
});

async function getTransformConfigs() {
    try {
        loadingList.value = true;

        await transformsStore.fetchTransformConfigs();
    } catch (error) {
        console.error('Error fetching transforms config', error);
    } finally {
        loadingList.value = false;
    }
}

const getClass = (control, index, advanced = false) => {
    const spaceLeft = control.space_left || 0; // Default depth is 0.
    const usedTransformConfigs = advanced ? visibleAdvancedTransformConfigControls.value : visibleBasicTransformConfigControls.value;
    const nextControlSpaceLeft = usedTransformConfigs[index + 1]?.space_left;

    const paddingLeftClass = spaceLeft > 0 ? `pl-${spaceLeft * 5}` : '';

    let marginBottomClass = 'mb-6'; // default margin for same level
    if (nextControlSpaceLeft === undefined) {
        // Last control: use standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft > spaceLeft) {
        // Next control is a child of the current control: smaller margin.
        marginBottomClass = 'mb-2';
    } else if (nextControlSpaceLeft === spaceLeft) {
        // Next control is at the same level: standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft < spaceLeft) {
        // Next control is at a higher level (ending a group): larger margin.
        marginBottomClass = 'mb-8';
    }

    return `${paddingLeftClass} ${marginBottomClass}`.trim();
};
</script>

<template>
    <div class="flex flex-col h-[calc(100vh-1rem)] overflow-hidden">
        <div class="page-header">
            <AppHeader gap="''" :show-title="false">
                <template #titleLeft>
                    <nav class="text-gray-700" aria-label="Breadcrumb">
                        <ol class="list-none p-0 inline-flex font-semibold">
                            <li class="flex items-center">
                                <router-link to="/transforms" class="text-green-500 hover:text-cyan-900">Transforms</router-link>
                                <ChevronRightIcon class="w-4 h-4 mx-2 text-cyan-950" />
                                <router-link to="/transforms/create" class="text-green-500 hover:text-cyan-900">Create Transform</router-link>
                                <ChevronRightIcon class="w-4 h-4 mx-2 text-cyan-950" />

                                <span v-if="transformTypeTitle" class="text-cyan-950">{{ transformTypeTitle }}</span>
                                <div v-else class="snippet mt-2 ml-2 flex items-baseline" data-title=".dot-flashing">
                                    <div class="stage">
                                        <div class="dot-flashing" />
                                    </div>
                                </div>
                            </li>
                        </ol>
                    </nav>
                </template>
            </AppHeader>
        </div>
        <div class="flex-1 overflow-auto">
            <AppLoader :force-loading="loadingList" default-height="75vh">
                <div v-if="transformTypeTitle && props.transformType" class="flex justify-between items-center mb-3">
                    <div class="flex flex-row gap-2 items-center">
                        <img
                            class="self-start h-15 w-15 flex-shrink-0"
                            :src="getTransformIcon(props.transformType)"
                            :alt="`${props.transformType} icon`" />
                        <span class="text-lg font-medium text-gray-800">{{ transformTypeTitle }}</span>
                    </div>
                </div>

                <div v-if="transformTypeConfig" class="flex flex-row justify-center w-full">
                    <div class="w-full">
                        <div class="bg-white p-4 shadow sm:rounded-lg sm:px-10">
                            <form
                                class="grid grid-cols-1 gap-y-3 sm:grid-cols-1 sm:gap-x-8 mt-5"
                                action="#"
                                method="POST"
                                autocomplete="new-password">
                                <div v-show="control.display_in_ui" v-for="(control, index) in visibleBasicTransformConfigControls" :key="index">
                                    <div :class="getClass(control, index)">
                                        <DynamicField
                                            :key="control.name"
                                            :config="control"
                                            :hide-lateral-description="false"
                                            @input="onFieldChanged" />
                                    </div>
                                </div>

                                <div v-if="visibleAdvancedTransformConfigControls.length > 0">
                                    <div>
                                        <h2 class="mb-0">
                                            <div
                                                tabindex="0"
                                                role="button"
                                                class="group relative flex w-full items-center font-medium p-2 rounded-t-lg text-md cursor-pointer hover:bg-green-50 transition-all duration-300 ease-in-out"
                                                :class="advancedMode ? 'bg-green-50' : ''"
                                                :aria-expanded="advancedMode"
                                                @click="advancedMode = !advancedMode"
                                                @keydown.enter="advancedMode = !advancedMode"
                                                @keydown.space.prevent="advancedMode = !advancedMode">
                                                Advanced
                                                <span class="ml-auto h-5 w-5 shrink-0 transition-transform duration-200 ease-in-out">
                                                    <ChevronUpIcon v-show="!advancedMode" class="h-5 w-5 text-gray-600"></ChevronUpIcon>
                                                    <ChevronDownIcon v-show="advancedMode" class="h-5 w-5 text-gray-600"></ChevronDownIcon>
                                                </span>
                                            </div>
                                        </h2>
                                        <div
                                            v-show="advancedMode"
                                            class="grid grid-cols-1 gap-y-6 sm:grid-cols-1 sm:gap-x-8 mb-5"
                                            :class="advancedMode ? 'border p-5 pt-8' : 'p-3'">
                                            <div
                                                v-show="control.display_in_ui"
                                                v-for="(control, index) in visibleAdvancedTransformConfigControls"
                                                :key="index">
                                                <div :class="getClass(control, index, true)">
                                                    <DynamicField
                                                        :key="control.name"
                                                        :config="control"
                                                        :hide-lateral-description="false"
                                                        @input="onFieldChanged" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </AppLoader>
        </div>
        <div class="mt-auto">
            <AppFooter v-if="transformTypeConfig">
                <template #right>
                    <div v-if="formError" class="flex flex-col text-md font-semibold items-end mr-10">
                        <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'create-transform'" />
                    </div>
                    <RowActionMenu
                        :show-save="true"
                        :show-discard="false"
                        :disable-save="!enableSave || loading"
                        :loading="loading"
                        @save="onSaveTransform" />
                </template>
            </AppFooter>
        </div>
    </div>
</template>

<style>
.arrow-left::before {
    content: '';
    position: absolute;
    left: -12px; /* Adjusted for the larger arrow */
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 18px solid transparent; /* increased for a taller arrow */
    border-bottom: 18px solid transparent; /* increased for a taller arrow */
    border-right: 12px solid #bbf7d0; /* adjusted to match the border color of the blue container */
}
</style>
